import { useState, useCallback, useMemo, useEffect } from 'react';
import { adminService } from '../../../services/adminService';
import { toast } from 'sonner';
import { buildCapMap, findOverCapLines } from '../../../lib/overOrder';

const DEFAULT_TIME = '7:00 AM - 9:00 AM';

// Encapsulates all state + handlers for the admin "New Order" / "Re-order" flow.
export const useNewOrder = ({ clients, onCreated }) => {
  const [newOrderOpen, setNewOrderOpen] = useState(false);
  const [newOrderClient, setNewOrderClient] = useState('');
  const [newOrderProducts, setNewOrderProducts] = useState([]);
  const [newOrderItems, setNewOrderItems] = useState([]);
  const [newOrderDate, setNewOrderDate] = useState('');
  const [newOrderTime, setNewOrderTime] = useState(DEFAULT_TIME);
  const [newOrderAddress, setNewOrderAddress] = useState('');
  const [newOrderPO, setNewOrderPO] = useState('');
  const [newOrderNotes, setNewOrderNotes] = useState('');
  const [creatingOrder, setCreatingOrder] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [clientAddresses, setClientAddresses] = useState([]);
  // Over-ordering: the bakery sees the same ceiling the client would, and
  // records a reason. An order the bakery books is never held.
  const [capInsights, setCapInsights] = useState(null);
  const [capReason, setCapReason] = useState('');
  const [capNote, setCapNote] = useState('');

  useEffect(() => {
    let ok = true;
    if (!newOrderOpen || !newOrderClient || !newOrderDate) { setCapInsights(null); return undefined; }
    adminService.getClientInsights(newOrderClient, newOrderDate)
      .then((res) => { if (ok) setCapInsights(res); })
      .catch(() => { if (ok) setCapInsights(null); });
    return () => { ok = false; };
  }, [newOrderOpen, newOrderClient, newOrderDate]);

  const loadProductsForClient = useCallback(async (clientId) => {
    setLoadingProducts(true);
    try {
      const prods = await adminService.getProductsForOrder(clientId || '');
      setNewOrderProducts(prods);
    } catch (e) {
      console.error('Error loading products:', e);
      toast.error('Failed to load products');
    } finally {
      setLoadingProducts(false);
    }
  }, []);

  const buildAddresses = useCallback((client) => {
    const allAddresses = [];
    (client?.locations || []).forEach(loc => {
      if (loc.address && !allAddresses.find(a => a.value === loc.address)) {
        // The site id travels with the choice, so the order is filed against
        // the site rather than reduced to its address.
        allAddresses.push({ label: loc.name || 'Location', value: loc.address, locationId: loc.id });
      }
    });
    return allAddresses;
  }, []);

  const handleOpenNewOrder = async () => {
    setNewOrderClient('');
    setNewOrderItems([]);
    setNewOrderDate('');
    setNewOrderTime(DEFAULT_TIME);
    setNewOrderAddress('');
    setNewOrderPO('');
    setNewOrderNotes('');
    setClientAddresses([]);
    setNewOrderOpen(true);
    await loadProductsForClient('');
  };

  // Quick re-order: clone a past order's items, time, address, PO and notes into
  // the New Order dialog. Delivery date is intentionally cleared because the
  // original date is in the past and the admin must pick a new one.
  const handleReorder = async (order) => {
    const clientId = order.client_id || order.client_account_id || '';
    setNewOrderClient(clientId);
    setNewOrderItems(
      (order.items || []).map(item => {
        const quantity = item.quantity || 1;
        const unitPrice = item.unit_price || 0;
        return {
          product_id: item.product_id,
          product_name: item.product_name,
          pack_size: item.pack_size || '',
          quantity,
          unit_price: unitPrice,
          original_price: item.original_price ?? unitPrice,
          discount_percent: item.discount_percent || 0,
          discount_amount: item.discount_amount || 0,
          has_discount: !!item.has_discount,
          subtotal: +(quantity * unitPrice).toFixed(2),
          image_url: item.image_url || '',
        };
      })
    );
    setNewOrderDate('');
    setNewOrderTime(order.delivery_time_window || DEFAULT_TIME);
    setNewOrderAddress(order.delivery_address || '');
    setNewOrderPO(order.po_number || '');
    setNewOrderNotes(order.order_notes || '');

    const client = clients.find(c => c.id === clientId);
    setClientAddresses(client ? buildAddresses(client) : []);

    setNewOrderOpen(true);
    await loadProductsForClient(clientId);
    toast.success('Order details copied. Pick a delivery date to confirm.');
  };

  const handleClientChange = async (clientId) => {
    setNewOrderClient(clientId);
    setNewOrderItems([]);
    await loadProductsForClient(clientId);
    const client = clients.find(c => c.id === clientId);
    if (client) {
      const allAddresses = buildAddresses(client);
      setClientAddresses(allAddresses);
      const defaultLoc = (client.locations || []).find(l => l.is_default);
      setNewOrderAddress(defaultLoc?.address || (allAddresses[0]?.value) || '');
    } else {
      setClientAddresses([]);
      setNewOrderAddress('');
    }
  };

  const handleNewOrderAddItem = (product) => {
    if (newOrderItems.find(i => i.product_id === product.id)) return;
    setNewOrderItems(prev => [...prev, {
      product_id: product.id,
      product_name: product.name,
      pack_size: product.pack_size,
      quantity: 1,
      unit_price: product.your_price,
      original_price: product.wholesale_price,
      discount_percent: product.discount_percent || 0,
      discount_amount: product.has_discount ? (product.wholesale_price - product.your_price) : 0,
      has_discount: product.has_discount,
      subtotal: product.your_price,
      image_url: product.image_url || '',
    }]);
  };

  const handleNewOrderQtyChange = (productId, qty) => {
    const q = Math.max(0, parseInt(qty) || 0);
    setNewOrderItems(prev => prev.map(i =>
      i.product_id === productId ? { ...i, quantity: q, subtotal: +(q * i.unit_price).toFixed(2) } : i
    ).filter(i => i.quantity > 0));
  };

  const handleNewOrderPriceChange = (productId, price) => {
    const p = parseFloat(price) || 0;
    setNewOrderItems(prev => prev.map(i =>
      i.product_id === productId ? { ...i, unit_price: p, subtotal: +(i.quantity * p).toFixed(2) } : i
    ));
  };

  const handleNewOrderRemoveItem = (productId) => {
    setNewOrderItems(prev => prev.filter(i => i.product_id !== productId));
  };

  const newOrderTotal = useMemo(
    () => newOrderItems.reduce((sum, i) => sum + (i.subtotal || 0), 0),
    [newOrderItems]
  );

  const capMap = useMemo(() => buildCapMap(capInsights), [capInsights]);
  const overCapLines = useMemo(
    () => findOverCapLines(newOrderItems, capMap),
    [newOrderItems, capMap],
  );
  const capGatingActive = !!capInsights?.gating?.active;
  const capReasonMissing = capGatingActive && overCapLines.length > 0 && !capReason;

  const handleCreateOrder = async () => {
    if (!newOrderClient) return toast.error('Please select a client');
    if (newOrderItems.length === 0) return toast.error('Please add at least one item');
    if (!newOrderDate) return toast.error('Please select a delivery date');
    if (capReasonMissing) {
      return toast.error('Pick a reason for the lines above this client\'s best day');
    }

    setCreatingOrder(true);
    try {
      const result = await adminService.adminCreateOrder({
        client_id: newOrderClient,
        items: newOrderItems,
        delivery_date: newOrderDate,
        delivery_time_window: newOrderTime,
        delivery_address: newOrderAddress,
        // Which saved site the chosen address belongs to, so the order carries
        // the client's own name for it.
        location_id: clientAddresses.find(a => a.value === newOrderAddress)?.locationId || null,
        po_number: newOrderPO,
        order_notes: newOrderNotes,
        ...(overCapLines.length > 0 && capReason
          ? { over_cap_reason: capReason, over_cap_note: capNote || null }
          : {}),
      });
      toast.success(result.message || 'Order created successfully');
      setNewOrderOpen(false);
      setCapReason('');
      setCapNote('');
      if (onCreated) await onCreated();
    } catch (error) {
      console.error('Error creating order:', error);
      toast.error(error.response?.data?.detail || 'Failed to create order');
    } finally {
      setCreatingOrder(false);
    }
  };

  return {
    newOrderOpen,
    setNewOrderOpen,
    newOrderClient,
    newOrderProducts,
    newOrderItems,
    newOrderDate,
    setNewOrderDate,
    newOrderTime,
    setNewOrderTime,
    newOrderAddress,
    setNewOrderAddress,
    newOrderPO,
    setNewOrderPO,
    newOrderNotes,
    setNewOrderNotes,
    newOrderTotal,
    clientAddresses,
    loadingProducts,
    creatingOrder,
    handleOpenNewOrder,
    handleReorder,
    handleClientChange,
    handleNewOrderAddItem,
    handleNewOrderQtyChange,
    handleNewOrderPriceChange,
    handleNewOrderRemoveItem,
    handleCreateOrder,
    // over-ordering
    capMap,
    overCapLines,
    capGatingActive,
    capReasons: capInsights?.cap_reasons,
    capReason,
    setCapReason,
    capNote,
    setCapNote,
    capReasonMissing,
  };
};
